/**
 * "What's new" — the curated highlight reel shown in the one-time version
 * popover (WhatsNewDialog) and the full /whats-new page.
 *
 * This is the *only* release-notes source — the docs/user-guide changelog it
 * used to sit alongside was removed on 2026-08-05. Keep each release to 1–3
 * short, user-facing bullets. When you cut a release *with a user-facing
 * change*, prepend an entry here (newest first). Internal-only patches don't
 * need one.
 */

/**
 * The version a note carries before it has one.
 *
 * A branch cannot know the version it will ship as — the version is computed at
 * merge time from the base branch, precisely so that two branches fixing two
 * things never collide on the same number. So a note written on a branch says
 * `NEXT`, and `scripts/bump-version.sh` rewrites it to the real version (and
 * fills in the date) in the same commit that sets VERSION.
 *
 * This is what keeps the "pin to the version it ships AS, never backfill" rule
 * true under a computed version: the pin still happens exactly once, at the
 * moment the version becomes known, rather than being guessed on the branch and
 * corrected later.
 *
 * An unpinned note is invisible everywhere a release is shown — the popover
 * (`pendingReleases`) and the /whats-new page both drop it. It describes a build
 * nobody is running.
 */
export const PENDING_VERSION = 'NEXT'

export interface ReleaseNote {
  /**
   * SemVer, matching the VERSION file / package.json for that release — or
   * `PENDING_VERSION` on a branch whose version has not been computed yet.
   */
  version: string
  /** ISO date the release shipped (display only). `PENDING_VERSION` until pinned. */
  date: string
  /** 1–3 short, user-facing highlights. */
  highlights: string[]
}

/** Every note that has a real version — i.e. everything except an unpinned one. */
export function shippedReleases(releases: ReleaseNote[] = RELEASES): ReleaseNote[] {
  return releases.filter((r) => r.version !== PENDING_VERSION)
}

/** Newest first. (Order is enforced by a test.) */
export const RELEASES: ReleaseNote[] = [
  {
    version: '2.9.88',
    date: '2026-11-04',
    highlights: [
      'You can now correct your own request after submitting it: the title, the description and the answers you gave stay editable until the request is closed.',
    ],
  },
  {
    version: '2.9.86',
    date: '2026-11-04',
    highlights: [
      'Filter the demand list and board by one or more caretakers, or find demands with no caretaker.',
      'DSAI assignment emails and notifications open the request with its caretaker and status controls. Cluster leads can also assign from a demand link.',
    ],
  },
  {
    version: '2.9.82',
    date: '2026-11-02',
    highlights: [
      'Planning distinguishes saved meeting edits from GitHub writeback, flags owner-account gaps early, and no longer treats completed work or unavailable idea slots as open decisions.',
    ],
  },
  {
    version: '2.9.75',
    date: '2026-10-29',
    highlights: [
      'Admins: Planning opens on a map. Each track is a lane, an epic is a band with its progress, priority is in colour, and hovering a card shows what it belongs to and what blocks it.',
      'Drag a card to move it, or open it to move it, name an owner and read or post comments.',
    ],
  },
  {
    version: '2.9.70',
    date: '2026-10-28',
    highlights: [
      'Admins: Planning has Present, one section at a time for a shared screen, and Decisions: what is still open and what the room decided.',
    ],
  },
  {
    version: '2.9.41',
    date: '2026-10-19',
    highlights: [
      'Applications stay open past the deadline. Council or the caretaker close them with Close next to Applications on the demand.',
      'New postings get a 30-day deadline instead of 7.',
    ],
  },
  {
    version: '2.9.35',
    date: '2026-10-16',
    highlights: [
      'Demands in Resourcing stay visible to everyone after the application window closes. Only applying closes.',
    ],
  },
  {
    version: '2.9.29',
    date: '2026-10-15',
    highlights: [
      "Admins: Apply to GitHub in the planning page's wrap-up writes the meeting's decisions to the board: next-cycle moves, new owners, picked ideas, and the read-back as a comment. You tick each change first.",
    ],
  },
  {
    version: '2.9.27',
    date: '2026-10-15',
    highlights: [
      'Admins: New cycle on the planning page opens a cycle still to come, straight from the GitHub board, so the meeting can plan the next one.',
      'On the planning page, "Parking lot" is now "Notes for later", so it is not confused with the demand stage.',
    ],
  },
  {
    version: '2.9.26',
    date: '2026-10-15',
    highlights: [
      'AI assistants stay connected for 24 hours, so you click Connect once a day.',
    ],
  },
  {
    version: '2.9.24',
    date: '2026-10-15',
    highlights: [
      'Admins: the Planning page reads the current cycle straight from the GitHub board, so there is no file to import. Refresh re-reads it.',
    ],
  },
  {
    version: '2.9.22',
    date: '2026-10-15',
    highlights: [
      'AI assistants connect with one click: run login in the DACcelerate AI tool and approve it in your browser. No more copying credentials.',
    ],
  },
  {
    version: '2.9.18',
    date: '2026-10-15',
    highlights: [
      'Admins: your AI assistant can now read the planning page and make the same changes you make there. Set it up under Help → AI access.',
    ],
  },
  {
    version: '2.9.14',
    date: '2026-10-14',
    highlights: [
      'One Monday email instead of several: what needs you, then the pipeline digest, in a single mail. Each part keeps its own switch under Emails in My profile.',
    ],
  },
  {
    version: '2.9.12',
    date: '2026-10-14',
    highlights: [
      'Council members and admins now get a weekly pipeline digest on Monday mornings: what was submitted, what opened, what closes soon, and what closed with nobody approved. Switch it off under Emails in My profile.',
    ],
  },
  {
    version: '2.9.4',
    date: '2026-10-13',
    highlights: [
      'Council members can now see and edit their skills on My profile.',
    ],
  },
  {
    version: '2.9.3',
    date: '2026-10-11',
    highlights: [
      'The Monday stalled-demand summary suggests a next step for each demand, one click from the email.',
    ],
  },
  {
    version: '2.9.1',
    date: '2026-10-11',
    highlights: [
      'Fewer emails: stalled demands, empty application windows and closing-soon reminders now come as one summary each, Monday 08:00 Central European time.',
      'Every email links to your email settings. Switch off the reminders you don’t need; mail about your own work still arrives.',
    ],
  },
  {
    version: '2.9.0',
    date: '2026-10-10',
    highlights: [
      'New demands arrive through one front door, and council routes each one with a reason.',
      'Requests that aren’t DAC matters go to DSAI, with a status, comments and reminders.',
      'Skills come from one shared list: six categories, 42 skills.',
    ],
  },
  {
    version: '2.8.242',
    date: '2026-10-09',
    highlights: [
      'Skills now sit in six categories. On your profile and on a demand, pick a whole category or one skill in it.',
      'Each skill says what it covers, with examples on hover.',
    ],
  },
  {
    version: '2.8.240',
    date: '2026-10-08',
    highlights: [
      'Council: the workspace feed uses current stage names and skips no-op moves; placeholder tiles and the dead Reports link are gone; the demand list opens on latest activity; Stage info shows the SLA as days left.',
      'Talents: opportunity cards show a summary, not the whole description; the workspace feed and tiles tidied.',
    ],
  },
  {
    version: '2.8.237',
    date: '2026-10-07',
    highlights: [
      'Talents and council: a first-run welcome and a Getting started checklist. Help now lists every message the app sends, and how it arrives.',
      'Talents: all four focus areas start selected, suggestions skip demands you can no longer act on, and each skill has a Notify me bell for "closing soon" emails.',
      'Council and DSAI: New demand offers Full or Simple form, actions say who will be told, and open DSAI requests are chased by email until someone looks after them.',
    ],
  },
  {
    version: '2.8.234',
    date: '2026-10-07',
    highlights: [
      'DSAI page: sort by any column. Each request has a status and comments that everyone on the page can see and add to.',
    ],
  },
  {
    version: '2.8.232',
    date: '2026-10-01',
    highlights: [
      'Council: the focus-area workspace reads top to bottom as what happened, then the numbers, then what you edit. Recent activity sits right under the tiles; Team, Tags and the Skills catalog are folded at the end and open on a click.',
      'Council: on a skill in the catalog, "N talents · M demands" opens in a dialog that scrolls, so a widely held skill does not push the rest of the list off the screen.',
      'On a demand, the section is called Skills. On your profile, each skill in the picker shows its focus area, or Shared.',
    ],
  },
  {
    version: '2.8.231',
    date: '2026-10-01',
    highlights: [
      'On a demand, a skill that is no longer in the catalog shows in amber with the reason on hover. It can still be removed, but not added back — until now it looked like every other skill.',
      'On your profile, typing a skill in full and pressing Enter adds it, the same as on a demand.',
    ],
  },
  {
    version: '2.8.230',
    date: '2026-10-01',
    highlights: [
      'Council: in the Skills catalog, the "N talents · M demands" on each skill opens to show who and which. Talents link to the Talent directory filtered on that skill; demands link to themselves.',
      'Skills that belong to no single focus area are called "Shared" rather than "Cross-FA".',
    ],
  },
  {
    version: '2.8.229',
    date: '2026-10-01',
    highlights: [
      'Council: the focus-area workspace shows the team as a count and opens the names on request, and the Meetings panel is gone. It showed one fixed sentence per focus area that nothing in the app could edit.',
    ],
  },
  {
    version: '2.8.228',
    date: '2026-10-01',
    highlights: [
      'The skills field on a demand can be browsed. Open it and the catalog is there; typing narrows it. It used to show nothing until you had typed two characters of a word from a list you had no way to look at.',
    ],
  },
  {
    version: '2.8.226',
    date: '2026-10-01',
    highlights: [
      'Council: moving a skill to another focus area now asks first. It used to happen the moment the dropdown changed, so a stray scroll handed a skill away — and once another focus area owns it you cannot take it back, which is exactly when an undo cannot help. The dialog says who will own it and whether you can reverse it.',
      'The Skills catalog has a search box, and typing a name that already exists says so while you type instead of refusing after you press Add.',
    ],
  },
  {
    version: '2.8.225',
    date: '2026-10-01',
    highlights: [
      'Email is back on, and a new opportunity posting no longer emails the whole talent pool. You will still see every posting in the bell; the announcement simply stops arriving in your inbox. Three postings had been generating over 600 emails, because most people are listed as interested in every focus area.',
      'Nothing from the three weeks email was paused is delivered retroactively. If you submitted a demand in that window and expected a receipt, it was recorded in the app but never sent.',
    ],
  },
  {
    version: '2.8.223',
    date: '2026-10-01',
    highlights: [
      'Choosing your focus areas now starts empty instead of pre-selecting all four. Pre-selecting them meant that opening the dialog and saving put you in the same audience as never answering it, so a single opportunity posting emailed over 200 people and "notify this focus area" narrowed nothing. Pick the ones you want; you can change it anytime in your profile.',
    ],
  },
  {
    version: '2.8.220',
    date: '2026-09-30',
    highlights: [
      'Council: renaming a skill or moving it to another focus area can be undone from the message that confirms it. Moving a skill used to happen silently. Note that giving a skill to another focus area gives away the right to change it, so that move cannot be undone — the message says so instead of offering a button that would fail.',
    ],
  },
  {
    version: '2.8.219',
    date: '2026-09-30',
    highlights: [
      'Mistyping a skill on your profile now suggests what you probably meant, and one click adds it. It used to say only that the word was not in the catalog — the demand form had offered the suggestion all along.',
      'Activating your talent profile says what it unlocks: listing your skills, applying to open positions, and appearing in the Talent directory.',
    ],
  },
  {
    version: '2.8.218',
    date: '2026-09-30',
    highlights: [
      'Renaming a skill in the catalog now updates the demands that asked for it, instead of leaving them on the old word. Each of those demands records the rename on its own timeline, so a caretaker can see who changed it and why. The panel says how many demands a rename will reach before you commit the new name.',
    ],
  },
  {
    version: '2.8.217',
    date: '2026-09-30',
    highlights: [
      'Adding a skill works the way you expect on every screen. Type a skill in full on your profile and Add takes it — it used to need a click in the dropdown and did nothing otherwise, without saying why. When a skill cannot be added, the button now states the reason.',
      'The skills field on a new demand offers the catalog rather than accepting anything typed. The catalog is what the server accepts, so a word it lacks used to be refused only at submit, after the whole form was filled.',
      'Opportunity cards no longer score you against skills we do not know. If your profile lists none, the page says so once and links you to it, instead of showing “0 of 5” on every card.',
      'Council: the Skills catalog panel now counts the demands using each skill, not only the talents, and says what a rename would leave behind.',
    ],
  },
  {
    version: '2.8.214',
    date: '2026-09-29',
    highlights: [
      'Opportunity cards and application forms show commitment and contact details. Save opportunities privately to return to them across visits.',
      'Opt into on-page reminders for saved or skill-matched opportunities closing within two days, without additional email or Teams messages.',
      'Applications show their last update and next step; council can jump to the oldest pending application to provide feedback.',
    ],
  },
  {
    version: '2.8.213',
    date: '2026-09-29',
    highlights: [
      'New on your profile: AI access. Connect an approved AI assistant to find opportunities, compare projects and check your applications. It can only read — it cannot apply, approve or change anything — and you can revoke it at any time.',
    ],
  },
  {
    version: '2.8.212',
    date: '2026-09-28',
    highlights: [
      'A demand’s skills now come from the skills catalog and nowhere else. Typing offers catalog entries, and a word that is not in there is not added — you are told what it is close to instead. Skills already on a demand stay, and can still be removed. Need a word the catalog lacks? Add it in your focus area’s Skills catalog panel first, and everyone gets it.',
    ],
  },
  {
    version: '2.8.211',
    date: '2026-09-24',
    highlights: [
      'The skills catalog is now curated: only a council or an admin can add a skill, and a new one has to name a focus area. On your profile, a skill the catalog lacks no longer offers to create it — it points you at your focus area’s council, who own the list.',
    ],
  },
  {
    version: '2.8.208',
    date: '2026-09-23',
    highlights: [
      'A quieter demand list with collapsible filters and quick previews. Browse a page from the preview, then return from details without losing your filters, sort or position.',
    ],
  },
  {
    version: '2.8.207',
    date: '2026-09-23',
    highlights: [
      'Less introductory text on Home and routing dialogs; email delivery diagnostics now sit below the log.',
      'Leadership viewers can browse the workspace and demand boards without editing controls. Request and support lists now work from the keyboard.',
      'Failed loads and confirmations offer a retry, draft exits ask before discarding, and removed roster entries can be restored. Opportunity filters and activity searches now reach beyond the first 200 records.',
    ],
  },
  {
    version: '2.8.205',
    date: '2026-09-23',
    highlights: [
      'Council: the demand intake form no longer shows an “intake quality” percentage. It was a second opinion about completeness that nothing acted on — the readiness checklist on the demand page is the one that matches what a stage move actually asks for.',
      'Every demand page now has a Discussion box for people who are not council: the person who submitted it, the caretaker looking after it, anyone working on it. The council reads everything written there; you see your own messages and any that mention you. A caretaker who is not on a council now reads and writes the whole discussion.',
    ],
  },
  {
    version: '2.8.204',
    date: '2026-09-22',
    highlights: [
      'DSAI cluster leads: “Sent to DSAI” is now in your sidebar, and you can open every demand in your cluster to name its caretaker.',
    ],
  },
  {
    version: '2.8.203',
    date: '2026-09-22',
    highlights: [
      'Submitting a demand now shows who it will be submitted as — your name, email and avatar — so you can check before you send. The two shortcuts under Sign in are now compact tiles; hover one to see what it does.',
    ],
  },
  {
    version: '2.8.202',
    date: '2026-09-22',
    highlights: [
      'Keyboard users in the Audit queue: Enter on "Move…", "See details" or any link now does what that control says. It used to accept the selected demand instead. Closing the Move dialog also returns you to the button you opened it from.',
    ],
  },
  {
    version: '2.8.200',
    date: '2026-09-22',
    highlights: [
      'Council: your focus area’s workspace now has a Skills catalog panel. Add a skill, rename one, or move it to another focus area or to the shared cross-FA pool — your own focus area’s entries and the cross-FA ones; admins curate all of them. Every rename and move is recorded with who made it.',
    ],
  },
  {
    version: '2.8.199',
    date: '2026-09-21',
    highlights: [
      'DSAI clusters now have a lead in the app — the counterpart of a focus area’s council lead, not a caretaker. When a demand is assigned to a cluster its lead is notified, and from the "Sent to DSAI" page they can name the caretaker for it or pass it to another cluster. They can change nothing else. Admins set who leads a cluster under All users → Edit; a lead does not have to be a council member.',
    ],
  },
  {
    version: '2.8.198',
    date: '2026-09-21',
    highlights: [
      'What the app called the "Digital Council" is now called DSAI, everywhere you read it. The Data & Analytics Council is itself part of DSAI, so it simply means "not a DAC matter": the request belongs with another part of the organisation. The page is at /dsai; old links still work.',
      'Council: a demand passed to DSAI now gets a DSAI cluster — Analytics (Nanda), CAIDO (Alex), Data (Kerstin), Digital (Klaus) or PT (Claudia) — and a caretaker, as the old New demand List had. Choose the cluster while routing, or afterwards on the "Sent to DSAI" page or on the demand itself. Every change lands on the Timeline.',
    ],
  },
  {
    version: '2.8.197',
    date: '2026-09-21',
    highlights: [
      'New here just to ask for something? Your home page now starts with Submit a demand and shows where your requests stand — no focus-area quiz, no talent dashboard. Activate a talent profile under My profile whenever you want those back.',
      'Council: the Audit queue entry in the sidebar now counts the new arrivals in your focus area that nobody has looked at, and your home page says the same. Until now the page read "0 waiting on you" while they waited.',
      'Council: "Assign to me" on an empty caretaker slot; the page the Digital Council reads is in the sidebar ("Sent to Digital Council") and linked from every routed demand, with a link per request and the requestor’s email.',
    ],
  },
  {
    version: '2.8.196',
    date: '2026-09-21',
    highlights: [
      'Submitted a demand? Your own page now shows everything you wrote — the anticipated benefit, who it is for and the background used to be hidden from the very person who typed them.',
      'When a demand is moved into your focus area, your council hears about it — with who moved it and why. Until now only brand-new submissions rang the bell, so a demand that arrived by being moved arrived silently.',
      'A request passed to the Digital Council now carries its reason everywhere: on their page, and on the demand’s Timeline — including requests the app routed by itself at submission, which used to say the requestor had sent them. The Timeline also reads in words ("Routed to: us → the Digital Council") instead of database values.',
      'Council: the "Problem missing" warning no longer fires on every submitted demand. The intake form asks one question for need and problem, and the checklist now counts it.',
    ],
  },
  {
    version: '2.8.185',
    date: '2026-09-11',
    highlights: [
      'Being a member no longer signs you up for staffing: new users see a simple profile and no talent pages. Want to browse and apply to open positions? One switch under My profile activates your talent profile — and lists you in the Talent directory so council can find you.',
    ],
  },
  {
    version: '2.8.184',
    date: '2026-09-11',
    highlights: [
      'Submitting a demand just got simpler: describe what you need and send it — no more picking a focus area. The council routes every submission to the right team itself, and you still get a receipt confirming what arrived.',
      'In the audit queue, "Looks right" and "Accept" were the same decision wearing two buttons — there is now one Accept. And a row you have already handled says so instead of offering its buttons again.',
    ],
  },
  {
    version: '2.8.178',
    date: '2026-09-10',
    highlights: [
      'The "Weekly pipeline digest" switch on your profile works now. It had never actually saved for anyone — flipping it always came back with an error — because of a leftover from the v1 database that rejected every attempt. Your choice sticks now, on the first try.',
      'The skills dropdown shows the whole catalogue while you browse. It used to stop at 50, which silently hid everything late in the alphabet — Spatial omics, Visium, Xenium — until enough other picks made room. Typing to search always worked; now browsing does too.',
    ],
  },
  {
    version: '2.8.176',
    date: '2026-09-10',
    highlights: [
      'The demand list and board now filter and count on the server. Every total you see is the truth about the whole filtered set — not just the rows that happened to load — and a board column showing part of itself says "20 of 61" instead of pretending. Sorting by Last activity is now correct across the entire set too.',
    ],
  },
  {
    version: '2.8.174',
    date: '2026-09-09',
    highlights: [
      'Moving a demand to another focus area no longer stops at the audit queue: open any demand and pick “Move to another focus area…” under More actions. Same dialog, same required reason — and if the demand already carries an id, it tells you the id stays as it is.',
    ],
  },
  {
    version: '2.8.173',
    date: '2026-09-09',
    // Corrected 2026-09-11: this entry originally described a submitter-facing
    // objection button and per-move notices, both refined away in the same UAT
    // cycle before ever reaching production. Rewritten to what actually ships,
    // because a promotion would otherwise deliver release notes describing
    // controls that do not exist.
    highlights: [
      'Submitting a demand now gets you a receipt — in the app and by email — confirming what was received. If your demand turns out to belong with the Digital Council, you get a note saying so and why.',
    ],
  },
  {
    version: '2.8.165',
    date: '2026-09-08',
    highlights: [
      'A new Audit queue in the sidebar shows every demand still in New, with the ones most worth a second look on top, and lets council move a demand to another focus area — or out to the Digital Council — with a reason. Moved something by mistake? The confirmation toast has an Undo.',
      'Requests that belong with the Digital Council now land on a page their people can read without knowing anything about this app: what was asked, who asked, and why we think it is theirs.',
    ],
  },
  {
    version: '2.8.163',
    date: '2026-08-31',
    highlights: [
      'Council members can now act on a demand in any focus area, not only their own. Outside your own area you are asked to confirm once — the demand says which area it belongs to — and then everything works as usual. Nothing is hidden from you any more because of where a demand sits, and every change you make is recorded under your name on the demand’s timeline, exactly as it is in your own area.',
    ],
  },
  {
    version: '2.8.161',
    date: '2026-08-31',
    highlights: [
      'The demand list has a Last activity column, so you can find what has been left alone longest. It counts anything that happened to a demand — a stage move, an application or a decision on one, an invitation, a comment, an edit — and sits next to the existing Days column in the same unit, so “40 days in stage, 2 days since anyone touched it” reads at a glance. Click the heading to sort the quietest demands to the top.',
    ],
  },
  {
    version: '2.8.160',
    date: '2026-08-31',
    highlights: [
      'Removing something now offers to put it back. Taking a tag off a demand, a skill off your profile, or an item out of a list used to happen instantly with no way back — each now says what went and gives you an Undo. The one exception is retracting an invitation, which asks first instead: it emails the person to say the invitation is withdrawn, so it is not something that can be quietly reversed.',
    ],
  },
  {
    version: '2.8.156',
    date: '2026-08-31',
    highlights: [
      'Marking a demand staffed can be taken back. It used to be a one-way door — one click, no confirmation, and nothing anywhere in the app could undo it; the only way out was closing the demand and re-opening it. There is now a Clear button beside the staffing status, and it also puts back the “days in stage” count that marking staffed had quietly reset to zero.',
    ],
  },
  {
    version: '2.8.155',
    date: '2026-08-31',
    highlights: [
      'Unsaved priority changes now wait for you. Adjusting a demand’s scores or bucket and then glancing at another tab used to throw the edits away without saying anything; they stay put now, and the Priority tab carries an "Unsaved" marker so you can see there is something to come back to.',
    ],
  },
  {
    version: '2.8.154',
    date: '2026-08-31',
    highlights: [
      'What you type into a field now survives leaving it. Editing a demand field and pressing Tab — or clicking anywhere else — used to silently put the old text back; it saves now, and Escape is how you throw an edit away. The Save button under the field also works from the keyboard, which it never did.',
      'Two other things stopped disappearing. A half-typed skill is no longer turned into a real one when you click away mid-selection — it waits in the box for you to finish — and closing the application form with something written in it now asks first, instead of taking your cover letter with it.',
    ],
  },
  {
    version: '2.8.153',
    date: '2026-08-31',
    highlights: [
      'Applicants can no longer be left waiting with nobody able to answer them. Moving a demand back out of Resourcing used to take the approve/decline controls off the page, while the Talent tab kept counting the people still pending — and unlike closing a demand, moving it back tells them nothing. The panel now stays for as long as someone is waiting, and says plainly that the demand has moved on.',
    ],
  },
  {
    version: '2.8.150',
    date: '2026-08-31',
    highlights: [
      'The board and the list can now be narrowed the same way. Both views filter by priority, demand type, support mode, staffing state, and — once a focus area is picked — its tags; the Kanban also gets the "My caretaking" toggle and a proper priority filter, and the list finally shows on-page controls for filters that used to exist only in the address bar.',
    ],
  },
  {
    version: '2.8.149',
    date: '2026-08-31',
    highlights: [
      'One pipeline in the sidebar again. The Product pipeline section is tucked away until the first product actually exists — it had been sitting there empty next to the demand pipeline, looking identical and meaning nothing. It returns by itself the moment a product is created.',
    ],
  },
  {
    version: '2.8.148',
    date: '2026-08-28',
    highlights: [
      'Several places stopped saying things that were not true. A withdrawn application is now shown as withdrawn to both you and the council — it used to read "pending review", so a council member could hold a seat open for someone who had already stepped away — and you can apply again. Your applications count now covers every row beneath it instead of two of the four statuses, and the demand list\'s "pending decisions" counts the demands you are actually looking at.',
      'Your profile completeness is now measured. It was fixed at 65% for everyone, and asked every talent to add a "publications link" that does not exist; it now reflects your bio, skills, focus areas and GitHub handle, and names the ones you are missing. The test-data switch labels the mode you are in rather than the one it would switch to, sorting My submissions by stage actually reorders the rows, and priority quadrants that cannot be picked no longer look like they can.',
    ],
  },
  {
    version: '2.8.147',
    date: '2026-08-28',
    highlights: [
      'Everything you can click, you can now reach with the keyboard. Applicant rows, talent and product cards, opportunity cards, similar-demand links and author names all looked like controls and announced themselves as controls, but Tab skipped past them — in the applications pane that meant you could review the first applicant and no other. Tab now reaches them and Enter or Space opens them.',
    ],
  },
  {
    version: '2.8.146',
    date: '2026-08-28',
    highlights: [
      'Several flows that used to have no way forward now have one. A link in a ticket notification opens that ticket instead of your home page; clicking a stage count in another focus area shows that area’s demands rather than your own; a page you cannot open says so instead of silently becoming your home page; you can withdraw an application even after the demand has been parked; and a council member who leads no single focus area is no longer held in the setup dialog.',
    ],
  },
  {
    version: '2.8.145',
    date: '2026-08-28',
    highlights: [
      'The Back button now works on filtered views. Filtering a timeline, switching a tab, or searching the demand list puts that choice in the address bar — but pressing Back only changed the address, leaving the page as it was. The page now follows the URL, so Back, Forward and a pasted link all show what they say.',
    ],
  },
  {
    version: '2.8.144',
    date: '2026-08-28',
    highlights: [
      'When something fails to load, the app now says so instead of showing you an empty page. A focus area with no activity and a focus area whose activity could not be fetched used to look identical — as did having no invitations and failing to check for them. Both now tell you what happened and offer to try again.',
    ],
  },
  {
    version: '2.8.143',
    date: '2026-08-28',
    highlights: [
      'Saving something now updates the screen you saved it on. A profile edit used to show the old text again the moment you navigated away and back; the readiness checklist kept asking for a field you had already filled in; and approving an applicant left the Staffing panel beside them still reading “0 of 3”. All three were the same kind of fault — the page was never told to re-read — and all three now refresh immediately.',
    ],
  },
  {
    version: '2.8.139',
    date: '2026-08-26',
    highlights: [
      'Being @mentioned now takes you to the comment. The notification used to open the demand’s Overview tab and leave you to find the thread; it now opens the Timeline on that exact comment. Following a timeline permalink also keeps the link intact — it used to clear itself as the page settled.',
      'The in-app guide covers the timeline and the activity feeds. It describes what the Timeline tab holds, how to filter and link to a single entry, that comments are permanent and only an admin can remove one, and where to watch your focus area — and every other focus area — from the Focus Areas page.',
    ],
  },
  {
    version: '2.8.137',
    date: '2026-08-26',
    highlights: [
      'Comments now say what they mean about permanence: they cannot be edited or deleted by their author, and an admin can remove one posted in error — a pasted secret, personal data — leaving a “[deleted]” marker.',
    ],
  },
  {
    version: '2.8.136',
    date: '2026-08-26',
    highlights: [
      'Focus areas now have a real activity feed. The workspace’s “Recent decisions” (5 stage moves) grew into the full stream — stage moves, applications, invitations, new intake and discussion — and the Focus Areas page now shows the latest activity across every FA. Discussion appears as “new discussion” only; comment contents stay within their focus area.',
    ],
  },
  {
    version: '2.8.134',
    date: '2026-08-26',
    highlights: [
      'Timeline views are now shareable. The tab you are on and any timeline filters live in the address bar, every entry has a permalink behind its timestamp, and a new person filter narrows the stream to one participant.',
    ],
  },
  {
    version: '2.8.133',
    date: '2026-08-26',
    highlights: [
      'The demand timeline now separates discussion from system events. Comments are cards with Markdown support — lists, links, code, and highlighted @mentions — while stage moves, field edits and people events sit as compact rows on a shared rail, grouped by day. The comment box moved to the top, next to the newest activity.',
    ],
  },
  {
    version: '2.8.132',
    date: '2026-08-26',
    highlights: [
      'The demand timeline now shows when a stage move went past warnings. A stage change that skipped expected information carries an amber “N warnings acknowledged” badge — expand it to see exactly what was skipped, named the way the warning dialog named it.',
    ],
  },
  {
    version: '2.8.118',
    date: '2026-08-25',
    highlights: [
      'When a button will not let you continue, it now tells you why. Creating a demand names the fields that are still empty — including when the block is on step 1 and you have already reached step 3 — and closing a demand names the one thing it is actually waiting for instead of asking you to redo work you have finished.',
    ],
  },
  {
    version: '2.8.114',
    date: '2026-08-25',
    highlights: [
      'Moving a demand between stages now tells you what is missing, and lets you go ahead anyway. Changing the stage from the demand page, and advancing or shelving one from Council Triage, used to show a message saying the missing information could be skipped — without listing any of it, and without actually offering to skip it, so the demand never moved. All three now list the fields grouped by the stage that expected them, with a Continue that goes through and records what was skipped.',
    ],
  },
  {
    version: '2.8.112',
    date: '2026-08-25',
    highlights: [
      'You can join the talent pool whatever your role. Being on a council never put you in it, and until now only talents had the switch — so council members and admins could not list themselves in the Talent directory or be told when a position opened. The “Available as talent” switch is now on every profile, and it warns you that, until you narrow your focus areas, every posting will email you. Admins can also set it for someone else from Admin → All Users.',
    ],
  },
  {
    version: '2.8.110',
    date: '2026-08-25',
    highlights: [
      'Someone can now be given read access to every focus area without being made a council member. Admins set it per person in Admin → All Users (“Read all focus areas”), and it grants reading only — no editing, no moving demands, and no weekly digest subscription. It is off for everyone until an admin turns it on, and turning it on or off is recorded in the admin audit log.',
    ],
  },
  {
    version: '2.8.107',
    date: '2026-08-25',
    highlights: [
      'A demand marked as staffed now reads as filled. When a council member marks a demand staffed — which is how work resourced outside the app gets recorded — the count beside it used to stay at 0 of 1, contradicting the “Fully Staffed” label right next to it. It now matches. Demands staffed through applications are unchanged, and a demand with more people than positions still shows that.',
    ],
  },
  {
    version: '2.8.104',
    date: '2026-08-25',
    highlights: [
      'Closing a demand as Promoted or Merged now works even when it is missing detail that earlier stages expected. Instead of a dead-end error, you see exactly which fields are missing and can either fill them in or confirm and close anyway — the same confirm-and-continue you already get when moving a demand between stages.',
    ],
  },
  {
    version: '2.8.103',
    date: '2026-08-25',
    highlights: [
      'A demand can be sent back to New. Demands brought in from elsewhere sometimes land in a later lane without ever having been reviewed, and there was no way to return them to the New intake queue where the focus area picks work up — so they sat unseen. Council can now move a demand back to New; because that undoes “review has started”, it asks for a quick confirmation first, and the move is recorded like any other.',
    ],
  },
  {
    version: '2.8.100',
    date: '2026-08-24',
    highlights: [
      'Screenshots pasted into a ticket now actually appear. They never did — pasting worked, the file uploaded, and then the image came back as a broken icon for everyone including you, because the app asked for it in a way that could not carry your sign-in. If you filed a ticket with a screenshot and it looked empty, the file was there the whole time and is there now. An attachment that genuinely cannot be loaded says which one and why, instead of showing a broken picture.',
    ],
  },
  {
    version: '2.8.97',
    date: '2026-08-21',
    highlights: [
      'A link to a demand you cannot open now tells you why. Opening one that had not been posted yet showed “Could not load demand” — the same red error as a genuine outage — so it read as a broken app rather than as work that simply is not open to you yet. It now says that a demand becomes visible once it is posted for applications, or once a focus area council invites you to it, and points you back to what is open. Real failures stay separate, and offer a retry.',
    ],
  },
  {
    version: '2.8.93',
    date: '2026-08-19',
    highlights: [
      'Tickets now track their own fix. Every ticket you file is mirrored to a GitHub issue, and when a maintainer closes that issue your ticket follows — resolved if it was fixed, closed if it was decided against, and back to in progress if the work reopens. The ticket links to its issue, so you can see what is actually happening rather than waiting for someone to remember to update two places.',
    ],
  },
  {
    version: '2.8.91',
    date: '2026-08-18',
    highlights: [
      'You can paste a screenshot straight into a ticket. Reporting something visual meant describing it in words, or filing the ticket and hoping someone asked. Paste or drag an image into the description — or into a comment on an existing ticket — and it is attached where you were writing about it. Tickets and comments also render Markdown now, so lists, code and links come out formatted instead of as the characters you typed. Tickets are visible to everyone signed in, so glance at what is on screen before you paste.',
    ],
  },
  {
    version: '2.8.90',
    date: '2026-08-18',
    highlights: [
      'An invitation to a demand that has moved on can now be cleared. If the work was parked, closed or sent back for more scoping after you were invited, the invitation stayed on your dashboard offering Accept and Decline — and both failed. It could only leave by someone withdrawing it or the demand coming back. Declining now always works, Accept is no longer offered when it cannot succeed, and the card says which of the two you are looking at.',
    ],
  },
  {
    version: '2.8.88',
    date: '2026-08-18',
    highlights: [
      'Being invited to a demand now means something wherever you click. Opening the demand and pressing “Apply” used to spend your invitation and put you in the review queue anyway — and a spent invitation could not be accepted any more, so there was no way back to the place the invitation had already given you. The demand page now offers “Accept invitation” when you have one, and if you apply instead, you join on the spot, as the invitation always meant you to.',
    ],
  },
  // The whole 2.8 line — the August cutover AND the twenty-eight patches after
  // it — rolled into one entry. Two things forced the merge rather than two
  // notes, and both are worth keeping written down.
  //
  // First, the cutover note was pinned to 2.8.38 and only ever reached SOME
  // readers. Dismissing the popover stores the APP's version, not the note's
  // (WhatsNewDialog: `writeSeen(CURRENT_VERSION)`), and pendingReleases shows
  // only notes strictly NEWER than what was stored. Anyone who dismissed a
  // popover after 2.8.38 shipped has a stored version above it, so the biggest
  // change in the app's history — the stage model — was already unreachable for
  // them. Re-stating it here is the only way they ever see it.
  //
  // Second, the patches after it went out with no note at all: 28 releases of
  // drift, invisible because nothing tied this file to VERSION.
  //
  // Hence the rule this entry follows: pin to the version it ships AS, never
  // backfill to a version already in the wild. The window is one-way. A guard in
  // whatsNew.test.ts enforces the half of that a machine can decide; whether a
  // release was user-visible at all stays a human check at release time.
  //
  // Since versions are computed at merge time, a new note leaves both `version`
  // and `date` as the quoted sentinel string, and the bump pins them. Do not
  // guess a number here; a guessed one is wrong the moment another branch
  // merges first.
  //
  // Write the quoted string, NOT the PENDING_VERSION constant. They are the same
  // value to TypeScript, which is exactly what makes the difference dangerous:
  // bump-version.sh and test.yml's promotion guard are shell, they read this
  // file as text, and a note written as the constant is invisible to both. It
  // ships unpinned, `shippedReleases()` then filters it out of every surface,
  // and the release goes out with a note nobody can see — silently, because the
  // guard whose job is to catch that missed it the same way. That is v2.8.96.
  // Both matchers now accept either spelling, so it can no longer fail quietly;
  // the quoted form is still what to write, because it is the one that greps.
  //
  // The trap in the other direction, from v2.8.87: this comment used to spell
  // the sentinel out, and the bump matched the comment instead of a release
  // entry — rewriting the instruction into a hardcoded version, so it told the
  // next author to do the exact thing it warns against. Both matchers are
  // anchored to a whole line now, which is what makes it safe to discuss the
  // sentinel here at all. Never leave one alone on a line, formatted the way a
  // real entry would be.
  {
    version: '2.8.84',
    date: '2026-08-15',
    highlights: [
      'If a demand you applied to is withdrawn, you now find out. Your application used to sit in “Your applications” looking perfectly ordinary — still pending, still showing the stage the demand was in — while opening it led nowhere and nobody was ever going to review it. Withdrawn demands are now labelled as such wherever your applications are listed, and you get a notification when it happens. Applications on demands withdrawn in the past are labelled too.',
    ],
  },
  {
    version: '2.8.67',
    date: '2026-08-14',
    highlights: [
      'Demands move through New → Scoping → Triaging → Resourcing → Closed, with Parking Lot alongside. Nothing moves by hand — your demands are already where they belong. Movement is now free: an incomplete demand is no longer blocked, you get a checklist of what is missing, and you can confirm and carry on. What you skipped is recorded on the demand. Parking also stops erasing a demand’s priority — it is still there when you bring it back.',
      'Opportunities now reach the talent pool. A posting used to be announced to the focus area’s council rather than to its talent, so people never heard about work they were a match for. The announcement itself now carries the skills, the time commitment, why it matters and the closing date, and a reminder goes out two days before the window shuts — the second chance to apply that never existed.',
      'Closing a demand now records how it ended. Resolved asks how the need was actually met — the council staffed it, you resourced it yourself, or it was met some other way — and Merged and Withdrawn stand on their own instead of being filed under “Other”. Between them those were three different endings sharing one label, so a closure report could not tell work that carries on elsewhere from work that stopped.',
      'The app tells you when nothing happened. Posting a demand now reports how many people it actually reached rather than how many it meant to, an application window that closes with nobody approved raises an alert, and a demand that sits in one stage for two weeks does too. Admins get a read-only page listing every notification the system can send and to whom, plus per-posting delivery results and a resend for anything that failed.',
      'When something goes wrong, the app now tells you what. Errors used to read “Request failed with status code 400” — the reason was there, the screen just threw it away. You now get the actual explanation: which rule stopped you, and what to change. Actions that used to fail with nothing on screen at all — withdrawing an application, accepting or declining an invitation — now say so, and accepting an invitation no longer reports an error after it has already worked. Server-side failures come with a reference you can quote to us.',
      'Some numbers were wrong and are now right — expect them to move. The admin closure breakdown and the focus-area workspace counted demands by a stage name that changed in the cutover, so they had been reading zero, and closed demands were being counted as active work. The overdue badges read zero for the same reason. Nothing about your data changed; the counting did.',
    ],
  },
  {
    version: '2.7.0',
    date: '2026-07-15',
    highlights: [
      'New — the Product pipeline. Promote a resolved demand into a Product and track it through PoC → MVP → Portfolio, with a 90-day gate review. Look for “Product pipeline” in the sidebar (list, detail, and board views).',
      'The demand Timeline is now filterable by event type and searchable, and it collapses noisy field-edit runs so the discussion stands out.',
      'Reliability & polish: lists that fail to load now offer “Try again” instead of a dead end, the notification bell always clears, Products and My Submissions are sortable and exportable to Excel, and your demand-list filters now survive Back and copy-link.',
    ],
  },
  {
    version: '2.6.2',
    date: '2026-07-14',
    highlights: [
      'Sort the demand list by more than one column — click a heading to sort, then Shift-click another to break ties (numbered badges show the order).',
    ],
  },
  {
    version: '2.5.17',
    date: '2026-06-30',
    highlights: [
      'See what’s new without leaving the app — a short note pops up after an update, and the new “What’s new” page (under Reference) has the full list.',
    ],
  },
  {
    version: '2.5.16',
    date: '2026-06-30',
    highlights: [
      'Tickets: click a ticket alert in the bell to jump straight to it.',
      'Admins can set a ticket’s priority (Low / Medium / High) and reassign it at triage.',
    ],
  },
  {
    version: '2.5.15',
    date: '2026-06-30',
    highlights: [
      'Report a problem from any page with the help button — it captures the page, app version, and browser for you.',
    ],
  },
  {
    version: '2.5.14',
    date: '2026-06-30',
    highlights: [
      'New Tickets page: report a bug, request a feature, or ask a question — then follow the thread.',
    ],
  },
  {
    version: '2.5.13',
    date: '2026-06-26',
    highlights: [
      'Batch approve/reject now reports the real outcome (e.g. “2 approved, 1 failed”) instead of assuming success.',
    ],
  },
  {
    version: '2.5.12',
    date: '2026-06-26',
    highlights: [
      'Accepting an invitation always seats you as a contributor — even after an earlier rejection.',
    ],
  },
  {
    version: '2.5.10',
    date: '2026-06-25',
    highlights: [
      'The admin Activity stream no longer shows up blank when a legacy record can’t be read.',
    ],
  },
  {
    version: '2.5.9',
    date: '2026-06-25',
    highlights: [
      'Test demands are a true sandbox now — they’re badged, tagged “[TEST]”, and never email real people.',
    ],
  },
]

/** "1.2.10" → [1, 2, 10]; non-numeric / missing parts collapse to 0. */
export function parseVersion(v: string): number[] {
  return (v || '').split('.').map((n) => parseInt(n, 10) || 0)
}

/** -1 / 0 / 1 like a comparator (a<b / a==b / a>b), tolerant of odd input. */
export function compareVersions(a: string, b: string): number {
  const pa = parseVersion(a)
  const pb = parseVersion(b)
  const len = Math.max(pa.length, pb.length)
  for (let i = 0; i < len; i++) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0)
    if (d !== 0) return d > 0 ? 1 : -1
  }
  return 0
}

/** The running app's version (compile-time define; falls back for safety). */
export const CURRENT_VERSION: string =
  typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : RELEASES[0]?.version ?? '0.0.0'

/**
 * Which releases the popover should show, given the version the user last
 * acknowledged (`seen`, null on first run):
 *  - first run → just the newest release (a nudge, not a history dump);
 *  - otherwise → every release newer than `seen` (newest first).
 */
export function pendingReleases(
  seen: string | null,
  // Injectable so the gating LOGIC can be tested against a fixture. It used to
  // read RELEASES directly, so its four tests hardcoded the real release list
  // and every added release note broke all four — which teaches people to edit
  // the expectation without reading it. Production callers pass nothing.
  releases: ReleaseNote[] = RELEASES,
): ReleaseNote[] {
  // An unpinned note has not shipped, so it is not news to anyone yet. Dropped
  // explicitly rather than left to fall out of the comparison below:
  // `parseVersion('NEXT')` is [0], so it would happen to sort as 0.0.0 and never
  // surface — correct by accident, and only until someone touches parseVersion.
  const shipped = shippedReleases(releases)
  if (!seen) return shipped.slice(0, 1)
  return shipped.filter((r) => compareVersions(r.version, seen) > 0)
}
